import type {ReactElement} from 'react';
import {Text, TextSize} from '@Components/text/text';
import {Button, Size, Type} from '@Components/button/button';
import {Pill} from '@Components/pill/pill';
import {PillSize, PillType} from '@Components/pill/pill.types';
import ClickableDiv from '@Components/clickable-div/clickable-div';
import {getComponentAction} from '@Components/ai-chat-window/lib/component-actions/component-action-registry';
import {useComponentActionEntries} from '@Components/ai-chat-window/lib/component-actions/component-action.hooks';
import type {OptionGridOption, OptionGridProps} from './option-grid.types';
import styles from './option-grid.module.scss';

export function OptionGrid({options, actionType, limitNotice}: OptionGridProps): ReactElement {
  const action = getComponentAction(actionType);
  const {doneTargetIds, startAction} = useComponentActionEntries(action, options);

  const renderUpgradeButton = (): ReactElement | null => {
    const openUpgrade = action?.openUpgrade;
    if (limitNotice?.showUpgrade !== true || openUpgrade === undefined) {
      return null;
    }

    return (
      <Button
        text={window.i18next.t('pmwjs_upgrade')}
        type={Type.PREMIUM}
        size={Size.XSMALL}
        icon="icon-crown"
        customClasses="radius-8"
        textClasses="_text-transform-none"
        onClick={openUpgrade}
      />
    );
  };

  const renderLimitNotice = (): ReactElement | null => {
    if (limitNotice === undefined) {
      return null;
    }

    return (
      <div className={styles.optionGridLimitNotice}>
        <Text val={limitNotice.text} size={TextSize.MD} className={styles.limitNoticeText} />
        {renderUpgradeButton()}
      </div>
    );
  };

  const renderOptionIcon = (option: OptionGridOption): ReactElement | null => {
    if (option.iconUrl === undefined || option.iconUrl === '') {
      return null;
    }

    return <img className={styles.optionIcon} src={option.iconUrl} alt="" aria-hidden="true" />;
  };

  const renderOptionText = (option: OptionGridOption): ReactElement => {
    return (
      <span className={styles.optionText}>
        <Text val={option.label} size={TextSize.MD} bold className={styles.optionLabel} />
      </span>
    );
  };

  const renderOption = (option: OptionGridOption): ReactElement => {
    if (action !== undefined && doneTargetIds.includes(option.id)) {
      return (
        <li key={option.id} className={`${styles.optionTile} ${styles.doneOptionTile}`}>
          {renderOptionIcon(option)}
          {renderOptionText(option)}
          <Pill label={window.i18next.t(action.doneLabelKey)} type={PillType.SUCCESS} size={PillSize.SM} />
        </li>
      );
    }

    return (
      <li key={option.id} className={styles.optionGridItem}>
        <ClickableDiv
          className={`${styles.optionTile} ${styles.clickableOptionTile}`}
          onClick={(): void => {
            startAction(option);
          }}
        >
          {renderOptionIcon(option)}
          {renderOptionText(option)}
        </ClickableDiv>
      </li>
    );
  };

  const renderOptions = (): ReactElement | null => {
    if (options.length === 0) {
      return null;
    }

    return (
      <ul className={styles.optionGridList} role="list">
        {options.map(renderOption)}
      </ul>
    );
  };

  return (
    <div className={styles.optionGrid}>
      {renderOptions()}
      {renderLimitNotice()}
    </div>
  );
}
